import {
  AfterViewInit,
  Component,
  EventEmitter, inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { OrganizationWithAccesses } from '../dto/InitDataDTO';
import { fadeInOutAnimation } from '../../../../util/animations/fade-in-out.animation';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { map, startWith, takeUntil } from 'rxjs/operators';
import { DEFAULT_DROP_DOWN_DELAY_TIME, DelayService } from "../../../service/delay.service";
import { OrganizationsService } from "../../../../domain/organization/service/organizations.service";
import { InputComponent } from "../../../../domain/shared/component/inputs/input-component/input.component";
import { AsyncPipe } from "@angular/common";
import { AllIconsComponent } from "../../../../domain/shared/component/all-icons/all-icons.component";
import { LinkShortPipe } from "../../../../util/pipes/link-short.pipe";
import { OrganizationDTO } from "../../../../domain/organization/dto/OrganizationDTO";

@Component({
  selector: 'app-org-picker',
  templateUrl: './org-picker.component.html',
  styleUrls: ['./org-picker.component.scss'],
  standalone: true,
  animations: [fadeInOutAnimation(200, 0)],
  providers: [DelayService],
  imports: [
    ReactiveFormsModule,
    InputComponent,
    AsyncPipe,
    AllIconsComponent,
    LinkShortPipe,
  ],
})

export class OrgPickerComponent implements OnInit, AfterViewInit {
  private _destroyStream: Subject<void> = new Subject<void>();
  private organizationsService = inject(OrganizationsService);
  private delayService = inject(DelayService);

  @Input() organizationsWithRoles: OrganizationWithAccesses[];
  @Input() currentService: string;
  @Input() isNewOrgCanBeCreated: boolean = false;
  @Output() orgSelectionEmitter = new EventEmitter<OrganizationWithAccesses>();
  @Output() createOrgEmitter = new EventEmitter<void>();

  showOrgPicker = false;
  selectedOrgOptions: Observable<OrganizationWithAccesses[]>;
  searchOrgControl: FormControl<string> = new FormControl<string>('');
  selectedOrg: OrganizationDTO;

  ngOnInit(): void {
    // After mouseEnter the input emits an empty value; therefore, we check the previous value from formControl
    this.selectedOrgOptions = this.searchOrgControl
      .valueChanges.pipe(
        takeUntil(this._destroyStream),
        startWith(''),
        map((value) => this._filterGroup(value || this.searchOrgControl.value)),
      );

    this.organizationsService.selected$
      .pipe(takeUntil(this._destroyStream))
      .subscribe((org) => {
        if (!org) {
          return;
        }
        this.selectedOrg = org;
      });
  }

  ngAfterViewInit(): void {
    this.delayService.initSubscribes(DEFAULT_DROP_DOWN_DELAY_TIME, this.showOrgDropDown, this.hideOrgDropDown);
  }

  activateOrgDropDown(): void {
    this.delayService.activate();
  }

  deactivateOrgPicker(): void {
    this.delayService.deactivate();
  }

  resetOrgInputValue(): void {
    this.searchOrgControl.setValue('');
  }

  private _filterGroup(searchInput: string): OrganizationWithAccesses[] {
    if (searchInput) {
      return this.filterOrgsBySearchString(this.organizationsWithRoles, searchInput);
    }
    return this.organizationsWithRoles;
  }

  private filterOrgsBySearchString =
    (opt: OrganizationWithAccesses[], searchInput: string): OrganizationWithAccesses[] => {
      const filterValue = searchInput.toLowerCase();
      return opt.filter(
        (item) => item.organization.name.toLowerCase().includes(filterValue),
      );
    };

  private showOrgDropDown: () => void = () => {
    this.showOrgPicker = true;
  };

  private hideOrgDropDown: () => void = () => {
    this.showOrgPicker = false;
  };
}
